import React, {ReactNode} from 'react'
import {FormattedDate} from 'react-intl'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {formatTimeZone} from '@vio/core/src/offer/business/cancellationPenalties'

import {Price} from '../../search/Price'
import {PenaltyContainer} from './PenaltyContainer'
import {
  currencyConsistentChecker,
  getPenaltyDateTime,
  NUMBER_OF_DECIMALS_FOR_PRICE
} from './utils'

interface NoRefundPeriodProps {
  date: string
  totalOfferPrice: number
  currency: string
  displayCurrencyCode: string
  hotelTimeZone?: string
  currencyStyle?: 'symbol' | 'code'
  isAuthenticateLater?: boolean | null
  renderTotalPrice?: (value: number, currencyCode: string) => ReactNode
}

export const NonRefundableInfo = ({
  isPostBooking = false
}: {
  isPostBooking?: boolean
}) => {
  return (
    <>
      <Box paddingBottom="s500" paddingTop="s300">
        <Text variant="bodyS">
          <FormattedMessageWrapper
            id="cancellation.nonRefundableDescription"
            defaultMessage="You won't be able to get a refund if you cancel or don’t show up."
          />
        </Text>
      </Box>
      {!isPostBooking && (
        <>
          <Tag
            variant="brand"
            size="sm"
            iconStart={<Icon name="Information" size="sm" />}
          >
            <Text variant="labelXS">
              <FormattedMessageWrapper
                id="cancellationPenalties.travelTip"
                defaultMessage="Travel tip"
              />
            </Text>
          </Tag>
          <Box paddingBottom="s500" paddingTop="s200">
            <Text variant="bodyS">
              <FormattedMessageWrapper
                id="cancellation.travelTipDescription"
                defaultMessage="Booking non-refundable rates can be a good way to {saveExtra} if you are certain your plans won’t change."
                values={{
                  saveExtra: (
                    <Text
                      colorPath="content.success.secondary"
                      variant="bodyS"
                      tag="span"
                    >
                      <FormattedMessageWrapper
                        id="cancellationPenalties.saveExtra"
                        defaultMessage="save extra"
                      />
                    </Text>
                  )
                }}
              />
            </Text>
          </Box>
        </>
      )}
    </>
  )
}

export const NoRefundPeriod = ({
  date,
  totalOfferPrice,
  currency,
  displayCurrencyCode,
  hotelTimeZone,
  currencyStyle,
  isAuthenticateLater = false,
  renderTotalPrice
}: NoRefundPeriodProps) => {
  const dateTime = getPenaltyDateTime(date, hotelTimeZone)
  const isCurrencyConsistent = currencyConsistentChecker(
    currency,
    displayCurrencyCode
  )

  return (
    <PenaltyContainer
      id="nonRefundablePeriod"
      date={
        <FormattedMessageWrapper
          defaultMessage="After {date}"
          id="cancellationPenalties.afterDate"
          values={{
            date: (
              <FormattedDate
                value={dateTime.date}
                month="short"
                day="2-digit"
                year="numeric"
              />
            )
          }}
        />
      }
      amount={
        isCurrencyConsistent ? (
          <div data-id="refundableCancellationPenalties.totalPrice">
            {renderTotalPrice ? (
              renderTotalPrice(Number(totalOfferPrice), displayCurrencyCode)
            ) : (
              <Price
                value={Number(totalOfferPrice)}
                currency={displayCurrencyCode}
                currencyDisplayVariant={currencyStyle}
                size="md"
                variant="regular"
                maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
                minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
              />
            )}
          </div>
        ) : null
      }
      timezone={formatTimeZone(dateTime.timeZone)}
      refundAmount={
        isCurrencyConsistent ? (
          <FormattedMessageWrapper
            id="cancellationPenalties.noRefund"
            defaultMessage="No"
          />
        ) : null
      }
      nonRefundable
      dateTime={dateTime.date}
      isAuthenticateLater={isAuthenticateLater}
    />
  )
}
